import { useEffect } from "react";
import { toast } from "sonner";
import { ShoppingCart, AlertTriangle, RefreshCw } from "lucide-react";
import { useMenuUpdateWatcher } from "@/hooks/useMenuUpdateWatcher";
import { type RestaurantMenu } from "@/hooks/useMenu";

interface MenuUpdateNotificationsProps {
  locationId: string;
  menuId?: string;
  enabled?: boolean;
}

export function MenuUpdateNotifications({
  locationId,
  menuId,
  enabled = true,
}: MenuUpdateNotificationsProps) {
  const { checkForMenuUpdates } = useMenuUpdateWatcher(locationId, menuId, {
    enabled,
    updateInterval: 30000, // 30 seconds
    onMenuUpdated: (_updatedMenu: RestaurantMenu) => {
      // Show a subtle notification that the menu was updated
      toast.info("Menu has been updated", {
        description: "",
        icon: <RefreshCw className="h-4 w-4" />,
        duration: 3000,
      });
    },
    onUnavailableItemsRemoved: (removedItems: string[]) => {
      // Show a persistent, prominent notification when items are removed from cart
      // Use a small delay to ensure these toasts appear after any menu update toasts
      setTimeout(() => {
        if (removedItems.length === 1) {
          toast.error("Item removed from cart", {
            description: `${removedItems[0]} is no longer available`,
            icon: <AlertTriangle className="h-4 w-4" />,
            duration: Infinity, // Don't auto-dismiss
            action: {
              label: "Dismiss",
              onClick: () => {},
            },
            richColors: true,
          });
        } else {
          toast.error("Items removed from cart", {
            description: `${removedItems.length} items are no longer available`,
            icon: <ShoppingCart className="h-4 w-4" />,
            duration: Infinity, // Don't auto-dismiss
            action: {
              label: "Dismiss",
              onClick: () => {},
            },
            richColors: true,
          });
        }
      }, 100); // Small delay to ensure it appears last
    },
  });

  // Expose manual refresh function globally (optional)
  useEffect(() => {
    // You could attach this to a global object or event if needed
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Check for updates when user returns to tab
        checkForMenuUpdates();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkForMenuUpdates]);

  // This component doesn't render anything visible - it only manages notifications
  return null;
}

// Custom toast styles for menu update notifications
export const menuUpdateToastStyles = {
  success: {
    background: "hsl(var(--success))",
    color: "hsl(var(--success-foreground))",
  },
  warning: {
    background: "hsl(var(--destructive))",
    color: "hsl(var(--destructive-foreground))",
  },
  info: {
    background: "hsl(var(--muted))",
    color: "hsl(var(--muted-foreground))",
  },
};
